import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import { AxiosResponse } from 'axios';
import { User } from '../types/userTypes';
import { BackendService } from './BackendService';
import AuthService from './AuthService';

interface RequiredMFA {
  status: Exclude<MfaStatusType, MfaStatusType.SUCCESS>;
  mfaToken: string;
  legacyLoginDisabled?: boolean;
}

interface LoginSuccess {
  status: MfaStatusType.SUCCESS;
  user: User;
}

interface MultiFactor extends LoginSuccess {
  mfaToken: string;
}

export default class UserService {
  static login(body: { email: string; password: string }) {
    return BackendService.post<User & { token: string }>('/users/login', body);
  }

  static oktaLogin(body: {
    state: string;
    code: string;
  }): Promise<AxiosResponse<LoginSuccess>> {
    return BackendService.post('/oidc/verify', body);
  }

  static oktaRequired(body: {
    email: string;
  }): Promise<AxiosResponse<{ oidcRequired: boolean }>> {
    return BackendService.post('/oidc/required', body);
  }

  static mfaLogin(body: {
    email: string;
    password: string;
  }): Promise<AxiosResponse<LoginSuccess | RequiredMFA>> {
    const mfaSessionToken = AuthService.getMfaSessionToken();

    if (mfaSessionToken) {
      const options = {
        headers: {
          Authorization: `Bearer ${mfaSessionToken}`,
        },
      };
      return BackendService.post<LoginSuccess | RequiredMFA>(
        '/users/mfalogin',
        body,
        options,
      );
    }

    return BackendService.post<RequiredMFA>('/users/mfalogin', body);
  }

  static multiFactor(payload: {
    rememberDevice: boolean;
    otp: string;
    mfaLoginToken: string;
  }) {
    const body = {
      rememberDevice: payload.rememberDevice,
      otp: payload.otp,
    };

    const options = {
      headers: {
        Authorization: `Bearer ${payload.mfaLoginToken}`,
      },
    };

    return BackendService.post<MultiFactor>(
      '/users/multiFactor',
      body,
      options,
    );
  }

  static resendOtp(payload: { mfaLoginToken: string }) {
    const options = {
      headers: {
        Authorization: `Bearer ${payload.mfaLoginToken}`,
      },
    };

    return BackendService.post<MultiFactor>('/users/resendOtp', null, options);
  }

  static getCurrentUser() {
    return BackendService.get<User>('/users/currentUserInfo');
  }

  static getAll(dealerId: string) {
    return BackendService.get<User[]>('/users/all', { params: { dealerId } });
  }

  static create(body: Omit<User, 'id'>) {
    return BackendService.post<User>('/users', body);
  }

  static update(id: number, body: Partial<User>) {
    return BackendService.put<User>(`/users/${id}`, body);
  }

  static delete(id: number) {
    return BackendService.delete(`/users/${id}`);
  }

  static changePassword(body: { email: string; newPassword: string }) {
    return BackendService.put<User>('/users/changepassword', body);
  }
}
